import React from 'react';
import { Button, Space, Popconfirm, Table } from 'antd';
import { EditOutlined, DeleteOutlined, EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

// ==================== 自动调整列宽的表格组件 ====================

interface ResizableTableProps<T> {
  columns: ColumnsType<T>;
  dataSource: T[];
  loading?: boolean;
  rowKey: string | ((record: T) => string);
  className?: string;
  scroll?: { x?: number | string | true; y?: number | string };
  pagination?: any;
}

export function ResizableTable<T extends object>({
  columns,
  dataSource,
  loading,
  rowKey,
  className,
  scroll,
  pagination,
}: ResizableTableProps<T>) {
  return (
    <Table
      columns={columns}
      dataSource={dataSource}
      loading={loading}
      rowKey={rowKey}
      className={className}
      scroll={scroll}
      pagination={pagination}
      tableLayout="auto"
    />
  );
}

// 格式化时间为 yyyy-MM-dd HH:mm:ss
export function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  const date = dayjs(dateStr);
  if (!date.isValid()) return dateStr;
  return date.format('YYYY-MM-DD HH:mm:ss');
}

// 格式化时间为 yyyy-MM-dd
export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  const date = dayjs(dateStr);
  if (!date.isValid()) return dateStr;
  return date.format('YYYY-MM-DD');
}

// 时间列渲染器
export function renderDateTime(text: string | null | undefined) {
  return <span style={{ color: '#6b7280', fontSize: 13 }}>{formatDateTime(text)}</span>;
}

// 日期列渲染器（只显示年月日）
export function renderDate(text: string | null | undefined) {
  return <span style={{ color: '#6b7280', fontSize: 13 }}>{formatDate(text)}</span>;
}

// 操作按钮组件
interface ActionButtonsProps {
  onEdit?: () => void;
  onDelete?: () => void;
  onView?: () => void;
  onDownload?: () => void;
  deleteTitle?: string;
  deleteDescription?: string;
  showEdit?: boolean;
  showDelete?: boolean;
  showView?: boolean;
  showDownload?: boolean;
}

export function ActionButtons({
  onEdit,
  onDelete,
  onView,
  onDownload,
  deleteTitle = '确定要删除吗？',
  deleteDescription = '删除后将无法恢复',
  showEdit = true,
  showDelete = true,
  showView = false,
  showDownload = false,
}: ActionButtonsProps) {
  return (
    <Space size={8}>
      {showDownload && onDownload && (
        <Button
          size="small"
          className="admin-action-btn download-btn"
          icon={<DownloadOutlined />}
          onClick={onDownload}
        >
          下载
        </Button>
      )}
      {showView && onView && (
        <Button
          size="small"
          className="admin-action-btn view-btn"
          icon={<EyeOutlined />}
          onClick={onView}
        >
          查看
        </Button>
      )}
      {showEdit && onEdit && (
        <Button
          size="small"
          className="admin-action-btn edit-btn"
          icon={<EditOutlined />}
          onClick={onEdit}
        >
          编辑
        </Button>
      )}
      {showDelete && onDelete && (
        <Popconfirm
          title={deleteTitle}
          description={deleteDescription}
          onConfirm={onDelete}
          okText="确定"
          cancelText="取消"
          okButtonProps={{ danger: true }}
        >
          <Button
            size="small"
            className="admin-action-btn delete-btn"
            icon={<DeleteOutlined />}
          >
            删除
          </Button>
        </Popconfirm>
      )}
    </Space>
  );
}

